import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Table, Badge, Alert, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import Navbar from '../../components/Navbar';
import formService from '../../services/formService';
import pdfService from '../../services/pdfService';
import usePagination from '../../hooks/usePagination';
import TablePagination from '../../components/TablePagination';

const JuniorInspectorDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [submissions, setSubmissions] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    submitted: 0,
    sentBack: 0,
    reviewed: 0,
    approved: 0
  });
  const [loading, setLoading] = useState(true);
  const [loadingPDF, setLoadingPDF] = useState(false);

  const { page, setPage, totalPages, paginatedItems: paginatedSubmissions } = usePagination(submissions);

  useEffect(() => {
    fetchSubmissions();
  }, []);

  const fetchSubmissions = async () => {
    try {
      setLoading(true);
      const response = await formService.getSubmissions();

      if (response.success) {
        const submissionsData = response.submissions.map(submission => ({
          id: submission.submission_id,
          form_number: submission.form_number,
          work_order_number: submission.work_order_number,
          client_name: submission.client_name,
          equipment_type: submission.equipment_type,
          inspection_date: submission.inspection_date,
          status: submission.current_status,
          comments: submission.latest_comments || null,
          sent_back_count: submission.sent_back_count || 0,
          certificate_number: submission.certificate_number || ''
        }));

        setSubmissions(submissionsData);

        // Calculate stats
        setStats({
          total: submissionsData.length,
          submitted: submissionsData.filter(s => s.status === 'submitted_by_junior').length,
          sentBack: submissionsData.filter(s => s.status === 'sent_back_by_senior').length,
          reviewed: submissionsData.filter(s => s.status === 'reviewed_by_senior').length,
          approved: submissionsData.filter(s => s.status === 'approved_by_admin').length
        });
      }

      setLoading(false);
    } catch (error) {
      console.error('Error fetching submissions:', error);
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      'submitted_by_junior': <Badge bg="primary">Submitted</Badge>,
      'sent_back_by_senior': <Badge bg="warning" text="dark">Sent Back</Badge>,
      'reviewed_by_senior': <Badge bg="info">Under Review</Badge>,
      'approved_by_admin': <Badge bg="success">Approved</Badge>
    };
    return badges[status] || <Badge bg="secondary">{status}</Badge>;
  };

  const handleViewDetails = (submissionId) => {
    navigate(`/submissions/${submissionId}`);
  };

  const handleEditSubmission = (submissionId) => {
    navigate(`/submission/edit/${submissionId}`);
  };

  const handleViewDataOnly = async (submission) => {
    try {
      setLoadingPDF(true);

      // Generate data-only preview (without seal and signatures)
      const response = await pdfService.dataOnlyPreview(submission.id);

      if (response.success) {
        // Download the data-only preview immediately
        const blob = await pdfService.downloadPreview(response.docx_filename);

        // Create download link
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = response.docx_filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        alert('Data-only preview downloaded successfully! (No seal or signatures included)');
      } else {
        alert('Error generating data-only preview: ' + response.message);
      }
    } catch (error) {
      console.error('Error generating data-only preview:', error);
      alert('Failed to generate data-only preview. Please try again.');
    } finally {
      setLoadingPDF(false);
    }
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <Container className="mt-5 text-center" style={{ paddingTop: '80px' }}>
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Loading dashboard...</p>
        </Container>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <Container fluid className="mt-4 mb-5" style={{ paddingTop: '40px' }}>
        {/* Welcome Section */}
        <Row className="mb-4">
          <Col>
            <h2 className="fw-bold text-primary">
              <i className="fas fa-tachometer-alt me-2"></i>
              Inspector Dashboard
            </h2>
            <p className="text-muted">
              Welcome back, <strong>{user?.full_name}</strong>! ({user?.emp_code})
            </p>
          </Col>
        </Row>

        {/* Statistics Cards */}
        <Row className="mb-4">
          <Col md={6} lg={3} className="mb-3">
            <Card className="text-center shadow-sm h-100">
              <Card.Body>
                <div className="mb-2">
                  <i className="fas fa-clipboard-list text-primary" style={{ fontSize: '2.5rem' }}></i>
                </div>
                <h3 className="fw-bold mb-0">{stats.total}</h3>
                <p className="text-muted mb-0">Total Submissions</p>
              </Card.Body>
            </Card>
          </Col>

          <Col md={6} lg={3} className="mb-3">
            <Card className="text-center shadow-sm h-100">
              <Card.Body>
                <div className="mb-2">
                  <i className="fas fa-paper-plane text-info" style={{ fontSize: '2.5rem' }}></i>
                </div>
                <h3 className="fw-bold mb-0">{stats.submitted}</h3>
                <p className="text-muted mb-0">Submitted</p>
              </Card.Body>
            </Card>
          </Col>

          <Col md={6} lg={3} className="mb-3">
            <Card className="text-center shadow-sm h-100">
              <Card.Body>
                <div className="mb-2">
                  <i className="fas fa-undo text-warning" style={{ fontSize: '2.5rem' }}></i>
                </div>
                <h3 className="fw-bold mb-0">{stats.sentBack}</h3>
                <p className="text-muted mb-0">Sent Back</p>
              </Card.Body>
            </Card>
          </Col>

          <Col md={6} lg={3} className="mb-3">
            <Card className="text-center shadow-sm h-100">
              <Card.Body>
                <div className="mb-2">
                  <i className="fas fa-check-circle text-success" style={{ fontSize: '2.5rem' }}></i>
                </div>
                <h3 className="fw-bold mb-0">{stats.approved}</h3>
                <p className="text-muted mb-0">Approved</p>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Action Buttons */}
        <Row className="mb-4">
          <Col>
            <div className="d-flex gap-3">
              <Button
                variant="primary"
                size="lg"
                onClick={() => navigate('/new-inspection')}
                className="shadow"
              >
                <i className="fas fa-plus-circle me-2"></i>
                New Inspection Form
              </Button>
              <Button
                variant="info"
                size="lg"
                onClick={() => navigate('/my-drafts')}
                className="shadow"
              >
                <i className="fas fa-save me-2"></i>
                My Drafts
              </Button>
            </div>
          </Col>
        </Row>

        {/* Submissions Table */}
        <Row>
          <Col>
            <Card className="shadow">
              <Card.Header className="bg-primary text-white">
                <h5 className="mb-0">
                  <i className="fas fa-list me-2"></i>
                  My Submissions
                </h5>
              </Card.Header>
              <Card.Body>
                {submissions.length === 0 ? (
                  <Alert variant="info">
                    <i className="fas fa-info-circle me-2"></i>
                    No submissions yet. Click "New Inspection Form" to create your first submission.
                  </Alert>
                ) : (
                  <div className="table-responsive">
                    <Table striped hover>
                      <thead>
                        <tr>
                          <th>Form Number</th>
                          <th>Work Order No</th>
                          <th>Client</th>
                          <th>Equipment Type</th>
                          <th>Inspection Date</th>
                          <th>Status</th>
                          <th>Corrections</th>
                          <th>Comments</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedSubmissions.map((submission) => (
                          <tr key={submission.id}>
                            <td>
                              <div className="fw-bold">{submission.form_number}</div>
                              {submission.certificate_number && (
                                <div className="text-muted small mt-1">
                                  <i className="fas fa-certificate me-1" style={{ fontSize: '0.7rem' }}></i>
                                  {submission.certificate_number}
                                </div>
                              )}
                            </td>
                            <td>{submission.work_order_number || '-'}</td>
                            <td>{submission.client_name}</td>
                            <td>{submission.equipment_type}</td>
                            <td>{new Date(submission.inspection_date).toLocaleDateString()}</td>
                            <td>{getStatusBadge(submission.status)}</td>
                            <td className="text-center">
                              {submission.sent_back_count > 0 ? (
                                <Badge
                                  bg={submission.sent_back_count >= 3 ? 'danger' : 'warning'}
                                  text={submission.sent_back_count >= 3 ? undefined : 'dark'}
                                  title={`Sent back ${submission.sent_back_count} time(s)`}
                                >
                                  <i className="fas fa-undo me-1"></i>
                                  {submission.sent_back_count}
                                </Badge>
                              ) : (
                                <span className="text-muted">—</span>
                              )}
                            </td>
                            <td>
                              {submission.comments ? (() => {
                                const styleMap = {
                                  'sent_back_by_senior': { background: '#fff3cd', color: '#664d03', border: '1px solid #ffda6a' },
                                  'approved_by_admin':   { background: '#d1e7dd', color: '#0a3622', border: '1px solid #a3cfbb' },
                                  'submitted_by_junior': { background: '#cfe2ff', color: '#084298', border: '1px solid #b6d4fe' },
                                  'reviewed_by_senior':  { background: '#cff4fc', color: '#055160', border: '1px solid #9eeaf9' },
                                };
                                const s = styleMap[submission.status] || { background: '#f8f9fa', color: '#495057', border: '1px solid #dee2e6' };
                                return (
                                  <span style={{
                                    ...s,
                                    display: 'inline-block',
                                    padding: '4px 8px',
                                    borderRadius: '4px',
                                    fontSize: '0.82em',
                                    lineHeight: '1.4',
                                  }}>
                                    <i className="fas fa-comment-dots me-1"></i>
                                    {submission.comments}
                                  </span>
                                );
                              })() : (
                                <span className="text-muted">—</span>
                              )}
                            </td>
                            <td>
                              <Button
                                variant="outline-primary"
                                size="sm"
                                className="me-2 mb-1"
                                onClick={() => handleViewDetails(submission.id)}
                                title="View Details"
                              >
                                <i className="fas fa-eye"></i>
                              </Button>
                              <Button
                                variant="info"
                                size="sm"
                                className="me-2 mb-1"
                                onClick={() => handleViewDataOnly(submission)}
                                disabled={loadingPDF}
                                title="View Data Only (No seal/signatures)"
                              >
                                <i className="fas fa-file-word"></i>
                              </Button>
                              {submission.status === 'sent_back_by_senior' && (
                                <Button
                                  variant="outline-warning"
                                  size="sm"
                                  className="mb-1"
                                  onClick={() => handleEditSubmission(submission.id)}
                                  title="Edit Submission"
                                >
                                  <i className="fas fa-edit"></i>
                                </Button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                    <TablePagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default JuniorInspectorDashboard;
